import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Navbar from "../components/Navbar";
import { TOOL_DOCS } from "../docs/toolDocs";
import { PURPOSES } from "../docs/purposeIndex";
import { Search, BookOpen, Compass, ChevronRight, Download } from "lucide-react";
import { exportHandbookPDF } from "../utils/exportHandbookPDF";

const CATEGORY_ORDER = ["Diagnose", "Design", "Implement", "Sustain", "People", "Domain"];

export default function DocsIndexPage() {
  const [query, setQuery] = useState("");
  const [purposeId, setPurposeId] = useState(null);

  const purpose = useMemo(() => PURPOSES.find((p) => p.id === purposeId), [purposeId]);

  const filtered = useMemo(() => {
    let list = TOOL_DOCS;
    if (purpose) list = list.filter((d) => purpose.tools.includes(d.id));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.tagline.toLowerCase().includes(q) ||
          d.purpose.toLowerCase().includes(q) ||
          (d.category || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [query, purpose]);

  const grouped = useMemo(() => {
    const acc = {};
    filtered.forEach((d) => {
      const k = d.category || "Other";
      acc[k] = acc[k] || [];
      acc[k].push(d);
    });
    return acc;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1 flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" /> Knowledge base
            </p>
            <h1 className="font-heading text-3xl font-semibold text-sage-900">TheKaizenCabinet — Tool Docs</h1>
            <p className="text-sage-500 text-sm mt-1">
              Comprehensive reference for all {TOOL_DOCS.length} tools — purpose, history, how-to, worked examples and pitfalls. Designed for new consultants.
            </p>
          </div>
          <button
            data-testid="docs-handbook-pdf"
            onClick={() => exportHandbookPDF({ firmName: "TheKaizenCabinet" })}
            title="Download all 39 tool docs as a single PDF handbook"
            className="inline-flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-sage-700 hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <Download className="w-4 h-4" /> Download Handbook PDF
          </button>
        </div>

        {/* Search */}
        <div className="bg-white border border-[#E2E8E3] rounded-md p-4 mb-5">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-sage-500" />
            <input
              data-testid="docs-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, purpose, or category…"
              className="flex-1 outline-none text-sm bg-transparent placeholder:text-sage-400"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-xs text-sage-500 hover:text-sage-700">
                clear
              </button>
            )}
          </div>
        </div>

        {/* Purpose finder */}
        <section data-testid="docs-purpose-finder" className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Compass className="w-4 h-4 text-sage-600" />
            <h2 className="font-heading text-sm font-semibold text-sage-900">Find a tool by purpose</h2>
            {purpose && (
              <button
                onClick={() => setPurposeId(null)}
                className="ml-auto text-xs text-sage-500 hover:text-sage-700"
              >
                clear filter
              </button>
            )}
          </div>
          <p className="text-xs text-sage-500 mb-3">Pick what you're trying to achieve — we'll suggest the right tool(s).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {PURPOSES.map((p) => {
              const isActive = p.id === purposeId;
              return (
                <button
                  key={p.id}
                  data-testid={`purpose-${p.id}`}
                  onClick={() => setPurposeId(isActive ? null : p.id)}
                  className={`text-left rounded-md border p-3 transition-all ${
                    isActive
                      ? "border-sage-600 bg-sage-50 ring-1 ring-sage-300"
                      : "border-sage-200 bg-white hover:border-sage-400"
                  }`}
                >
                  <p className="font-medium text-sm text-sage-900">{p.label}</p>
                  <p className="text-xs text-sage-500 mt-0.5">{p.blurb}</p>
                  <p className="text-[11px] text-sage-400 mt-1.5">{p.tools.length} tools</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Tool list grouped by category */}
        <div className="space-y-6" data-testid="docs-list">
          {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
            <section key={cat}>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-sage-600 mb-3">{cat}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped[cat].map((d) => {
                  const Icon = Icons[d.icon] || Icons.BookOpen;
                  return (
                    <Link
                      key={d.id}
                      data-testid={`doc-link-${d.id}`}
                      to={`/docs/${d.id}`}
                      className="group bg-white border border-[#E2E8E3] rounded-md p-4 flex items-start gap-3 hover:border-sage-400 hover:shadow-sm transition-all"
                    >
                      <div className="w-9 h-9 bg-sage-100 text-sage-600 rounded-md flex items-center justify-center flex-shrink-0 group-hover:bg-sage-600 group-hover:text-white transition-colors">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-sage-900 truncate">{d.name}</p>
                        <p className="text-xs text-sage-500 mt-0.5 line-clamp-2">{d.tagline}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-sage-300 group-hover:text-sage-500 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-sage-500 text-sm border border-dashed border-sage-200 rounded-md bg-white">
              No tools match your search. Try a different keyword or clear the purpose filter.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
